<template>
  <div class="chips">
    <div
      v-for="(val, key, index) in selectValues"
      ref="content"
      :key="index"
      class="chips__content"
    >
      <span class="chips__key">
        {{ key }}
      </span>

      <span
        v-if="showParams(val)"
        class="chips__value"
      >
        : {{ getParams(val) }}
      </span>

      <Badge
        v-if="showBadge(val)"
        :value="val.length.toString()"
      />

      <DsButton
        v-if="!readOnly"
        icon
        color="ghost"
        name="close"
        size="small"
        class="chips__icon"
        @click="removeValue(key)"
      />
    </div>

    <div
      v-if="showNonvisibleFilterCounter && invisibleValuesCount > 0"
      ref="badge"
      class="chips__filter-badge"
    >
      <Badge
        :value="invisibleValuesCount.toString()"
        :preset="true"
      />
    </div>
  </div>
</template>

<script>
import { DsButton } from '@ds/components';
import Badge from '@/components/Badge';

export default {
  name: 'Chips',
  components: {
    DsButton,
    Badge,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    maxPrintedValues: {
      type: Number,
      default: Number(0),
    },
    showNonvisibleFilterCounter: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectValues: [],
      invisibleValuesCount: 0,
      badgePosition: 0,
    };
  },
  watch: {
    value(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.selectValues = newValue;
    },
  },
  mounted() {
    this.selectValues = this.value;
  },
  updated() {
    if (this.showNonvisibleFilterCounter && this.$refs.content?.length) {
      const firstChieldOffset = this.$refs.content[0].offsetTop;
      const lastChieldOffset = this.$refs.content[this.$refs.content.length - 1].offsetTop;

      this.invisibleValuesCount = this.$refs.content
        .filter(({ offsetTop }) => firstChieldOffset < offsetTop).length;

      const visibleValues = this.$refs.content
        .filter(({ offsetTop }) => lastChieldOffset > offsetTop);

      if (visibleValues.length) {
        this.referenceBadge = visibleValues[visibleValues.length - 1];
        if (this.$refs.badge) {
          this.$refs.badge.style.left = `${this.referenceBadge.offsetLeft + this.referenceBadge.offsetWidth}px`;
        }
      }
    }
  },
  methods: {
    showParams(params) {
      return (params.length || 1) <= this.maxPrintedValues;
    },
    getParams(params) {
      if (!params.length) {
        return params;
      }

      return params.slice(0, this.maxPrintedValues).toString();
    },
    showBadge(params) {
      return params.length > this.maxPrintedValues;
    },
    removeValue(key) {
      this.$emit('remove', key);
    },
  },
};
</script>

<style scoped lang="scss">
  .chips {
    display: flex;
    position: relative;
    align-content: flex-start;
    flex-wrap: wrap;
    overflow: hidden;
    padding: 2px var(--ds-spacing-stack-nano) 0 0;
    row-gap: 10px;

    &__content {
      height: 24px;
      display: flex;
      align-items: center;
      max-width: 334px;
      padding: 0 var(--ds-spacing-stack-nano);
      margin: 0 var(--ds-spacing-stack-quark);
      background: var(--ds-color-overlay);
      border: 1px solid var(--ds-color-gray-2);
      border-radius: 24px;
    }

    &__key {
      font-size: var(--ds-font-size-xs);
      line-height: 16px;
      font-weight: var(--ds-font-weight-regular);
    }

    &__value {
      font-size: var(--ds-font-size-xs);
      line-height: 16px;
      font-weight: var(--ds-font-weight-semibold);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__icon {
      margin-left: var(--ds-spacing-stack-quark);
    }

    &__filter-badge {
      position: absolute;
      top: 0;
    }
  }

  ::v-deep {
    .ds-button--icon {
      height: 24px !important;
      width: 16px !important;
      padding: 0;
    }

    .ds-button--ghost {
      &:hover {
        background-color: transparent;
        box-shadow: none;
      }

      &:focus {
        background-color: transparent;
        box-shadow: none !important;
      }

      .ds-icon {
        background-color: var(--ds-color-icon-2);
      }
    }
  }
</style>
